'use client';

export type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

const CONSENT_KEY = 'labflow_consent_v1';

export interface ConsentRecord {
  version: '1.0';
  timestamp: string; // ISO 8601
  region: ConsentRegion;
  choices: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  userAgent: string;
}

/**
 * Detect user's region based on browser locale and timezone
 * No external API calls — pure client-side detection
 */
export function detectRegion(): ConsentRegion {
  if (typeof navigator === 'undefined') return 'gdpr'; // SSR default

  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

  const euTZPrefixes = [
    'Europe/',
    'Atlantic/Azores',
    'Atlantic/Canary',
    'Atlantic/Faroe',
    'Atlantic/Madeira',
    'Arctic/Longyearbyen',
  ];

  const euCountryCodes = [
    'AT',
    'BE',
    'BG',
    'CY',
    'CZ',
    'DE',
    'DK',
    'EE',
    'ES',
    'FI',
    'FR',
    'GR',
    'HR',
    'HU',
    'IE',
    'IT',
    'LT',
    'LU',
    'LV',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SE',
    'SI',
    'SK',
    'GB',
    'IS',
    'LI',
    'NO',
    'CH',
  ];

  const isEU =
    euTZPrefixes.some((p) => tz.startsWith(p)) ||
    euCountryCodes.some((c) => lang.toUpperCase().endsWith('-' + c));

  const isUS = tz.startsWith('America/') && (lang.startsWith('en-US') || lang.startsWith('en-CA'));

  if (isEU) return 'gdpr';
  if (isUS) return 'ccpa';
  return 'none';
}

/**
 * Get consent record from localStorage
 */
export function getConsentRecord(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as ConsentRecord;
  } catch (error) {
    console.error('Error reading consent record:', error);
    return null;
  }
}

/**
 * Save consent record to localStorage
 */
export function saveConsentRecord(record: ConsentRecord): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch (error) {
    console.error('Error saving consent record:', error);
  }
}

/**
 * Create a new consent record
 */
export function createConsentRecord(
  region: ConsentRegion,
  choices: Omit<ConsentRecord['choices'], 'necessary'>
): ConsentRecord {
  return {
    version: '1.0',
    timestamp: new Date().toISOString(),
    region,
    choices: {
      necessary: true,
      ...choices,
    },
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

/**
 * Check if user has given consent for a specific category
 */
export function hasConsent(category: 'analytics' | 'marketing' | 'preferences'): boolean {
  const record = getConsentRecord();
  if (!record) return false;
  return record.choices[category] ?? false;
}

/**
 * Log consent action for audit trail
 */
export function logConsentAction(
  action: 'banner_shown' | 'accept_all' | 'reject_all' | 'preferences_saved' | 'ccpa_dismiss',
  region: ConsentRegion
): void {
  if (typeof window === 'undefined') return;

  try {
    const logKey = `${CONSENT_KEY}_log`;
    const logs = JSON.parse(localStorage.getItem(logKey) || '[]') as Array<{
      timestamp: string;
      action: string;
      region: ConsentRegion;
    }>;

    logs.push({
      timestamp: new Date().toISOString(),
      action,
      region,
    });

    // Keep only last 100 entries
    if (logs.length > 100) {
      logs.shift();
    }

    localStorage.setItem(logKey, JSON.stringify(logs));
  } catch (error) {
    console.error('Error logging consent action:', error);
  }
}

/**
 * Clear all consent data
 */
export function clearConsent(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(CONSENT_KEY);
    localStorage.removeItem(`${CONSENT_KEY}_log`);
  } catch (error) {
    console.error('Error clearing consent:', error);
  }
}
